import type { DtActionsAtom } from '@/components/pages/backoffice/_klb-penyuluh/_kecamatan/state/atoms'
import type {
  ServiceGetAllResponse,
  ServiceGetByIdResponse,
  ServiceMutationOptionType,
  ServiceQueryOptionType
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type { PotensiEkonomiDataType } from './types'
import type { MutationPotensiEkonomiValues } from './validations'

const QUERY_KEY = (key?: string | Record<string, any>) =>
  ['potensi-ekonomi', key] as const

const getAll = (
  params?: DtActionsAtom
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<PotensiEkonomiDataType>>
> => ({
  queryKey: QUERY_KEY(params),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<PotensiEkonomiDataType>>
      >(`kelembagaan/bpp/${params?.id_bpp}/potensi-ekonomi`, {
        params: {
          page: params?.page,
          per_page: params?.perPage || undefined
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getById = (
  id: string,
  id_bpp?: string
): ServiceQueryOptionType<ServiceGetByIdResponse<PotensiEkonomiDataType>> => ({
  queryKey: QUERY_KEY(id),
  queryFn: async () => {
    try {
      if (!id_bpp) {
        throw new Error('id_bpp is required')
      }
      const response = await api.get<
        ServiceGetByIdResponse<PotensiEkonomiDataType>
      >(`kelembagaan/bpp/${id_bpp}/potensi-ekonomi/${id}`)
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const create = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PotensiEkonomiDataType>,
  MutationPotensiEkonomiValues
> => ({
  mutationKey: QUERY_KEY(undefined),
  mutationFn: async (data: MutationPotensiEkonomiValues) => {
    try {
      const response = await api.post(
        `kelembagaan/bpp/${data.id_bpp ?? ''}/potensi-ekonomi`,
        {
          ...data,
          id_potensi_ekonomi: data.id_potensi_ekonomi.value
        }
      )

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const update = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PotensiEkonomiDataType>,
  MutationPotensiEkonomiValues
> => ({
  mutationKey: QUERY_KEY(undefined),
  mutationFn: async (data: MutationPotensiEkonomiValues) => {
    try {
      const response = await api.put<
        ServiceGetByIdResponse<PotensiEkonomiDataType>
      >(
        `kelembagaan/bpp/${data?.id_bpp ?? ''}/potensi-ekonomi/${data.id_potensi}`,
        {
          ...data,
          id_potensi_ekonomi: data.id_potensi_ekonomi.value
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteData = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PotensiEkonomiDataType>,
  { id: string; id_bpp: string }
> => ({
  mutationFn: async (data: { id: string; id_bpp: string }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<PotensiEkonomiDataType>
      >(`kelembagaan/bpp/${data?.id_bpp ?? ''}/potensi-ekonomi/${data.id}`)
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

export const potensiEkonomi = {
  queryKey: [QUERY_KEY(undefined)[0]],
  getAll,
  getById,
  create,
  update,
  deleteData
}
